// ============================================
// AlpenScreen - Super Admin Organizations Component
// File: frontend/src/components/SuperAdminOrganizations.js
// Purpose: View and manage all organizations
// ============================================

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Building2, 
  Users, 
  Monitor,
  Plus,
  AlertCircle,
  CheckCircle,
  Calendar,
  X,
  Trash2  // ✅ NEU
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SuperAdminOrganizations = () => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);  // ✅ NEU

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load organizations');

      const data = await response.json();
      setOrganizations(data);
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (org) => {
    setSelectedOrg(org);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedOrg(null);
  };

  // ✅ NEU: Handler für Organisation erstellen
  const handleCreateOrg = () => {
    console.log('🎯 Opening create organization modal');
    setShowCreateModal(true);
  };

  const handleCloseCreate = () => {
    setShowCreateModal(false);
  };

  // ✅ NEU: Handler für neue Organisation erstellen
  const handleSubmitCreate = async (formData) => {
    console.log('📤 Creating new organization:', formData);
    
    // ✅ Map frontend field names to backend field names
    const backendPayload = {
      organization_name: formData.name,
      organization_color: formData.color || '#9333ea',
      admin_email: formData.email,
      admin_password: formData.password,
      subscription_tier: formData.subscription_tier,
      trial_days: formData.trial_days
    };
    
    console.log('📦 Backend payload:', backendPayload);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/organizations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(backendPayload)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Organization created:', data);
        alert('Organisation erfolgreich erstellt!');
        loadOrganizations(); // Reload list
        handleCloseCreate();
      } else {
        const error = await response.json();
        console.error('❌ Error creating organization:', error);
        alert(`Fehler: ${error.error || 'Organisation konnte nicht erstellt werden'}`);
      }
    } catch (error) {
      console.error('❌ Catch error:', error);
      alert('Fehler beim Erstellen der Organisation');
    }
  };

  const handleUpdateOrg = async (orgId, updates) => {
    console.log('🔧 Updating organization:', orgId);
    console.log('📦 Updates:', updates);
    
    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/superadmin/organizations/${orgId}`;
      
      console.log('🌐 API URL:', url);
      console.log('🔑 Token exists:', !!token);
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Success response:', data);
        alert('Organisation erfolgreich aktualisiert!');
        loadOrganizations();
        handleCloseDetails();
      } else {
        const errorData = await response.json();
        console.error('❌ Error response:', errorData);
        alert(`Fehler: ${errorData.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('❌ Catch error:', error);
      alert('Fehler beim Aktualisieren der Organisation');
    }
  };

  // ✅ NEU: Handler für Organisation löschen
  const handleDeleteOrg = async (orgId, orgName) => {
    console.log('🗑️ Attempting to delete organization:', orgId, orgName);
    
    // Double confirmation
    const confirmText = `Möchten Sie die Organisation "${orgName}" wirklich UNWIDERRUFLICH löschen?\n\n` +
                       `⚠️ WARNUNG: Dies wird auch alle Benutzer, Bildschirme und Daten dieser Organisation löschen!\n\n` +
                       `Geben Sie den Namen der Organisation ein um zu bestätigen: "${orgName}"`;
    
    const userInput = prompt(confirmText);
    
    if (userInput !== orgName) {
      if (userInput !== null) { // User didn't cancel
        alert('Der eingegebene Name stimmt nicht überein. Löschvorgang abgebrochen.');
      }
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/organizations/${orgId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert(`Organisation "${orgName}" erfolgreich gelöscht!`);
        loadOrganizations();
        handleCloseDetails();
      } else {
        const error = await response.json();
        console.error('❌ Error deleting organization:', error);
        alert(`Fehler: ${error.error || 'Organisation konnte nicht gelöscht werden'}`);
      }
    } catch (error) {
      console.error('❌ Catch error:', error);
      alert('Fehler beim Löschen der Organisation');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      trial: { color: 'bg-blue-100 text-blue-800', label: 'Trial' },
      expired: { color: 'bg-red-100 text-red-800', label: 'Expired' },
      cancelled: { color: 'bg-gray-100 text-gray-800', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-purple-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Organisationen
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Alle registrierten Organisationen verwalten
          </p>
        </div>
        <button 
          onClick={handleCreateOrg}  // ✅ NEU: onClick Handler hinzugefügt
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Organisation erstellen
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md">
          <div className="text-sm text-gray-600 dark:text-gray-400">Total</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {organizations.length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md">
          <div className="text-sm text-gray-600 dark:text-gray-400">Active</div>
          <div className="text-2xl font-bold text-green-600">
            {organizations.filter(o => o.subscription_status === 'active').length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md">
          <div className="text-sm text-gray-600 dark:text-gray-400">Trial</div>
          <div className="text-2xl font-bold text-blue-600">
            {organizations.filter(o => o.subscription_status === 'trial').length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md">
          <div className="text-sm text-gray-600 dark:text-gray-400">Expired</div>
          <div className="text-2xl font-bold text-red-600">
            {organizations.filter(o => o.subscription_status === 'expired').length}
          </div>
        </div>
      </div>

      {/* Organizations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {organizations.map((org) => (
          <div
            key={org.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden"
          >
            {/* Organization Header */}
            <div 
              className="h-24 bg-gradient-to-br from-purple-500 to-purple-600 relative"
              style={{ backgroundColor: org.color || '#9333ea' }}
            >
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="text-xl font-bold text-white truncate">
                  {org.name}
                </h3>
              </div>
            </div>

            {/* Organization Body */}
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Status</span>
                {getStatusBadge(org.subscription_status)}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Tier</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 capitalize">
                  {org.subscription_tier || 'starter'}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Users size={16} />
                <span>{org.user_count || 0} Benutzer</span>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Monitor size={16} />
                <span>{org.screen_count || 0} Bildschirme</span>
              </div>

              {org.trial_end_date && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Calendar size={16} />
                  <span>Trial: {new Date(org.trial_end_date).toLocaleDateString('de-CH')}</span>
                </div>
              )}

              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <button 
                  onClick={() => handleViewDetails(org)}
                  className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Details anzeigen
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {organizations.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Keine Organisationen
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Es wurden noch keine Organisationen erstellt.
          </p>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedOrg && (
        <OrganizationDetailsModal
          org={selectedOrg}
          onClose={handleCloseDetails}
          onUpdate={handleUpdateOrg}
          onDelete={handleDeleteOrg}  // ✅ NEU
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateOrganizationModal
          onClose={handleCloseCreate}
          onSubmit={handleSubmitCreate}
        />
      )}
    </div>
  );
};

// ============================================
// Create Organization Modal (✅ NEU)
// ============================================
const CreateOrganizationModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    color: '#9333ea',  // ✅ NEU: Default color
    subscription_tier: 'starter',
    trial_days: 14
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.password) {
      alert('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    if (formData.password.length < 6) {
      alert('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setCreating(true);
    await onSubmit(formData);
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Neue Organisation erstellen
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Organization Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Organisations-Informationen
            </h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organisation Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="z.B. Firma AG"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organisation Farbe
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="h-10 w-20 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formData.color}
                </span>
                <div 
                  className="h-10 flex-1 rounded"
                  style={{ backgroundColor: formData.color }}
                />
              </div>
            </div>
          </div>

          {/* Admin User Info */}
          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Administrator Benutzer
            </h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                E-Mail Adresse *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="admin@firma.ch"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Passwort *
              </label>
              <input
                type="password"
                required
                minLength={6}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Mindestens 6 Zeichen"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Der Benutzer wird als E-Mail verifiziert erstellt
              </p>
            </div>
          </div>

          {/* Subscription Settings */}
          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Subscription Einstellungen
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subscription Tier
                </label>
                <select
                  value={formData.subscription_tier}
                  onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="starter">Starter (CHF 29)</option>
                  <option value="business">Business (CHF 99)</option>
                  <option value="enterprise">Enterprise (CHF 299)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Trial Tage
                </label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={formData.trial_days}
                  onChange={(e) => setFormData({ ...formData, trial_days: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Info:</strong> Die Organisation startet automatisch mit einem {formData.trial_days}-tägigen Trial.
                  Der Admin-Benutzer kann sich sofort einloggen (E-Mail ist verifiziert).
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="submit"
              disabled={creating}
              className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              {creating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Organisation wird erstellt...
                </>
              ) : (
                <>
                  <Plus size={20} />
                  Organisation erstellen
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={creating}
              className="flex-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg font-medium transition-colors"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// Organization Details Modal
// ============================================
const OrganizationDetailsModal = ({ org, onClose, onUpdate, onDelete }) => {  // ✅ NEU: onDelete prop
  const [formData, setFormData] = useState({
    name: org.name || '',
    subscription_status: org.subscription_status || 'trial',
    subscription_tier: org.subscription_tier || 'starter'
  });
  const [extendDays, setExtendDays] = useState(14);
  const [extending, setExtending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(org.id, formData);
  };

  // ✅ NEU: Delete handler
  const handleDelete = () => {
    onDelete(org.id, org.name);
  };

  const handleExtendTrial = async () => {
    if (!extendDays || extendDays < 1) {
      alert('Bitte geben Sie eine gültige Anzahl Tage ein');
      return;
    }

    setExtending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/organizations/${org.id}/extend-trial`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days: parseInt(extendDays) })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Trial erfolgreich um ${extendDays} Tage verlängert!`);
        onClose();
        window.location.reload();
      } else {
        const error = await response.json();
        alert(error.error || 'Fehler beim Verlängern des Trials');
      }
    } catch (error) {
      console.error('Error extending trial:', error);
      alert('Fehler beim Verlängern des Trials');
    } finally {
      setExtending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Organisation Details: {org.name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Grundinformationen
            </h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organisation Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subscription Status
                </label>
                <select
                  value={formData.subscription_status}
                  onChange={(e) => setFormData({ ...formData, subscription_status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subscription Tier
                </label>
                <select
                  value={formData.subscription_tier}
                  onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="starter">Starter (CHF 29)</option>
                  <option value="business">Business (CHF 99)</option>
                  <option value="enterprise">Enterprise (CHF 299)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Stats (Read-only) */}
          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Statistiken
            </h4>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">Benutzer</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {org.user_count || 0}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">Bildschirme</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {org.screen_count || 0}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">Erstellt</div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {new Date(org.created_at).toLocaleDateString('de-CH')}
                </div>
              </div>
            </div>
          </div>

          {/* Trial Info + Extension */}
          {org.trial_end_date && (
            <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Trial Period
              </h4>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <div>
                    <div className="font-medium text-blue-900 dark:text-blue-100">
                      Current Trial End
                    </div>
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      {new Date(org.trial_end_date).toLocaleDateString('de-CH')}
                    </div>
                  </div>
                </div>

                {/* Trial Extension */}
                <div className="flex gap-2 mt-4">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={extendDays}
                    onChange={(e) => setExtendDays(e.target.value)}
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="14"
                  />
                  <button
                    type="button"
                    onClick={handleExtendTrial}
                    disabled={extending}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {extending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Verlängern...
                      </>
                    ) : (
                      <>
                        <Calendar size={18} />
                        Trial um {extendDays} Tage verlängern
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* Primary Actions */}
            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Änderungen speichern
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Abbrechen
              </button>
            </div>

            {/* Danger Zone - Delete */}
            <div className="pt-3 border-t border-red-200 dark:border-red-800">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <h5 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">
                  ⚠️ Danger Zone
                </h5>
                <p className="text-xs text-red-700 dark:text-red-300 mb-3">
                  Das Löschen der Organisation entfernt ALLE Benutzer, Bildschirme und Daten unwiderruflich!
                </p>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 size={18} />
                  Organisation löschen
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuperAdminOrganizations;